import { Box, Text, Container } from "@chakra-ui/react";
import { motion } from "framer-motion";
import { Calendar, AlertCircle } from "lucide-react";
import styled, { useTheme } from "styled-components";
import Select from "react-select";
import { useSchedule } from "../context/ScheduleProvider";
import { AppTheme } from "../theme";
import { THEME } from "../helpers/constans";

type YearEntry = {
  id: string;
  value: string;
};

const MotionBox = motion(Box);

const EmptyStateContainer = styled(Container)<{ $theme: AppTheme }>`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 1.5rem 1.5rem;
  min-height: calc(100vh - 270px);
  text-align: center;
`;

const EmptyStateCard = styled(MotionBox)<{ $theme: AppTheme }>`
  background: ${({ $theme }) => $theme.backgroundCard};
  border: 1px solid ${({ $theme }) => $theme.borderCard};
  border-radius: ${THEME.borderRadiusXl};
  box-shadow: ${({ $theme }) => $theme.shadowLg};
  padding: 2rem 1.5rem;
  max-width: 500px;
  width: 100%;
  position: relative;
  overflow: hidden;
`;

const DecorativeBlob = styled.div<{ $theme: AppTheme; $position: "top" | "bottom" }>`
  position: absolute;
  ${({ $position }) => $position === "top" ? "top: -60px; right: -60px;" : "bottom: -60px; left: -60px;"}
  width: 200px;
  height: 200px;
  border-radius: 50%;
  background: ${({ $theme }) => `${$theme.primary}10`};
  filter: blur(40px);
  z-index: 0;
`;

const IconWrapper = styled.div<{ $theme: AppTheme }>`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 70px;
  height: 70px;
  border-radius: 50%;
  background: ${({ $theme }) => `${$theme.primary}15`};
  color: ${({ $theme }) => $theme.primary};
  margin: 0 auto 1rem;
  position: relative;
  z-index: 1;
`;

const YearSelectWrapper = styled.div<{ $theme: AppTheme }>`
  margin-top: 1.5rem;
  position: relative;
  z-index: 1;
`;

const getSelectStyles = (theme: AppTheme) => ({
  control: (base: any, state: any) => ({
    ...base,
    background: theme.background2,
    borderColor: state.isFocused ? theme.primary : theme.border,
    borderWidth: "2px",
    borderRadius: THEME.borderRadius,
    padding: "8px 12px",
    boxShadow: state.isFocused ? `0 0 0 3px ${theme.primary}20` : theme.shadowSm,
    transition: "all 0.2s ease",
    fontFamily: "'Plus Jakarta Sans', sans-serif",
    fontSize: "16px",
    minHeight: "48px",
    "&:hover": {
      borderColor: theme.primary,
    },
  }),
  option: (base: any, state: any) => ({
    ...base,
    background: state.isSelected 
      ? theme.primary 
      : state.isFocused 
        ? `${theme.primary}15`
        : theme.background2,
    color: state.isSelected ? "white" : theme.text,
    padding: "14px 16px",
    cursor: "pointer",
    fontFamily: "'Plus Jakarta Sans', sans-serif",
    fontSize: "16px",
    fontWeight: state.isSelected ? 600 : 500,
    transition: "background 0.15s ease",
    minHeight: "44px",
    "&:active": {
      background: `${theme.primary}25`,
    },
  }),
  menu: (base: any) => ({
    ...base,
    background: theme.background2,
    border: `1px solid ${theme.border}`,
    borderRadius: THEME.borderRadius,
    boxShadow: theme.shadowLg,
    overflow: "hidden",
    zIndex: 9999,
  }),
  menuPortal: (base: any) => ({
    ...base,
    zIndex: 9999,
  }),
  menuList: (base: any) => ({
    ...base,
    padding: 0,
  }),
  placeholder: (base: any) => ({
    ...base,
    color: theme.textMuted,
    fontWeight: 500,
  }),
  singleValue: (base: any) => ({
    ...base,
    color: theme.text,
    fontWeight: 600,
  }),
  input: (base: any) => ({
    ...base,
    color: theme.text,
  }),
  indicatorSeparator: () => ({
    display: "none",
  }),
  dropdownIndicator: (base: any, state: any) => ({
    ...base,
    color: state.isFocused ? theme.primary : theme.textMuted,
    transition: "all 0.2s ease",
    transform: state.selectProps.menuIsOpen ? "rotate(180deg)" : "rotate(0)",
    "&:hover": {
      color: theme.primary,
    },
  }),
});

export function EmptyStateTemplate() {
  const theme = useTheme() as AppTheme;
  const { availableYears, selectedYearId, setSelectedYearId } = useSchedule();
  const selectStyles = getSelectStyles(theme);

  const hasYearSelection = (availableYears as YearEntry[]).length > 0;
  const years = (availableYears as YearEntry[]) || [];

  return (
    <EmptyStateContainer maxW="container.md" $theme={theme}>
      <EmptyStateCard
        $theme={theme}
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, ease: [0.4, 0, 0.2, 1] }}
      >
        <DecorativeBlob $theme={theme} $position="top" />
        <DecorativeBlob $theme={theme} $position="bottom" />
        
        <Box position="relative" zIndex={1}>
          <IconWrapper $theme={theme}>
            <Calendar size={32} />
          </IconWrapper>

          <Text
            fontFamily="'Outfit', sans-serif"
            fontSize="lg"
            fontWeight="700"
            color={theme.text}
            mb={2}
            lineHeight="short"
          >
            Brak harmonogramu dla wybranego okresu
          </Text>

          <Text
            color={theme.textSecondary}
            fontSize="sm"
            lineHeight="tall"
            fontWeight="500"
            mb={hasYearSelection ? 2.5 : 0}
          >
            Aktualnie nie posiadamy dostępu do harmonogramu wywozu odpadów dla wybranego roku.
            {hasYearSelection && (
              <>
                <br />
                <br />
                Możesz wybrać inny rok z listy poniżej.
              </>
            )}
          </Text>

          {hasYearSelection && (
            <YearSelectWrapper $theme={theme}>
              <Box mb={2}>
                <Text
                  fontSize="sm"
                  fontWeight="600"
                  color={theme.text}
                  mb={2}
                  display="flex"
                  alignItems="center"
                  gap={2}
                  justifyContent="center"
                >
                  <Calendar size={16} />
                  Wybierz rok
                </Text>
              </Box>
              <Select
                placeholder="Wybierz rok..."
                options={years.map((year: YearEntry) => ({
                  value: year.id,
                  label: year.value
                }))}
                value={selectedYearId ? {
                  value: selectedYearId,
                  label: years.find((y: YearEntry) => y.id === selectedYearId)?.value || ""
                } : null}
                styles={selectStyles}
                menuPortalTarget={document.body}
                menuPosition="fixed"
                onChange={(e: any) => {
                  if (e) {
                    setSelectedYearId(e.value);
                  }
                }}
                noOptionsMessage={() => "Brak dostępnych lat"}
              />
            </YearSelectWrapper>
          )}

          {!hasYearSelection && (
            <Box mt={4} pt={4} borderTop={`1px solid ${theme.border}`}>
              <Box
                display="flex"
                alignItems="center"
                justifyContent="center"
                gap={2}
                color={theme.textMuted}
                fontSize="sm"
              >
                <AlertCircle size={16} />
                <Text>
                  Pracujemy nad aktualizacją danych, które wkrótce pojawią się w aplikacji.
                </Text>
              </Box>
            </Box>
          )}
        </Box>
      </EmptyStateCard>
    </EmptyStateContainer>
  );
}
